import { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { useAppDispatch } from '@/redux/hooks';
import { getProductByFoodId } from '@/redux/slices/product.slice';

type Size = {
  sizeType: string;
  image?: string;
  dataBig: {
    keyFormula: number;
    id?: string;
    data: {
      keyComponent: number,
      id?: string;
      ingredient: { name: string, label: string },
      weight: { name: string },
      processingMethod: { name: string },
    }[]
  }[]
}

// Hook quản lý cache cho sản phẩm AVN theo foodIds
export const useProductCache = (sizes: Size[]) => {
  const dispatch = useAppDispatch();
  const [avnProducts, setAvnProducts] = useState<Product[]>([]);
  const productCache = useRef<Map<string, Product[]>>(new Map());

  const currentFoodIds = useMemo(() => {
    const foodIds = sizes.flatMap(size =>
      size.dataBig.flatMap(formula =>
        formula.data.map(component => {
          const ingredient = component.ingredient;
          return typeof ingredient === 'object' && ingredient !== null && 'name' in ingredient
            ? ingredient.name
            : ingredient;
        })
      )
    ).filter(Boolean);

    return Array.from(new Set(foodIds));
  }, [sizes]);

  const fetchProductsWithCache = useCallback(async (foodIds: string[]) => {
    if (foodIds.length === 0) {
      setAvnProducts([]);
      return;
    }

    const cachedProducts: Product[] = [];
    const uncachedFoodIds: string[] = [];

    for (const foodId of foodIds) {
      const products = productCache.current.get(foodId);
      if (products) {
        cachedProducts.push(...products);
      } else {
        uncachedFoodIds.push(foodId);
      }
    }
    
    if (uncachedFoodIds.length === 0) {
      setAvnProducts(cachedProducts);
      return;
    }

    try {
      const results = await Promise.all(
        uncachedFoodIds.map(async (foodId) => {
          const products = await dispatch(getProductByFoodId(foodId)).unwrap();
          productCache.current.set(foodId, products);
          return products;
        })
      );

      const allProducts = [...cachedProducts, ...results.flat()];
      setAvnProducts(allProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
      setAvnProducts(cachedProducts);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchProductsWithCache(currentFoodIds);
  }, [currentFoodIds, fetchProductsWithCache]);

  return avnProducts;
};
